import { useRef, useCallback, useState, useEffect } from "react"
import {
	ForgeRockStep,
	ForgeRockAuthService,
	resetJourneyCache
} from "@services/forgeRockAuthService"
import { StoredStep, StepNavigationMethods } from "@models/forgeRock.model"
import {
	generateJourneyId,
	createLogStepInfo,
	createLogNavigationAttempt,
	createLogStepsHistory,
	exposeNavigationMethodsToWindow
} from "@utils/forgeRock.util"

export const useForgeRockNavigation = (
	onError?: (error: Error) => void,
	skipInitialJourney?: boolean
) => {
	// ===== STATE MANAGEMENT =====
	const [currentStep, setCurrentStep] = useState<ForgeRockStep | null>(null)
	const [currentStepNumber, setCurrentStepNumber] = useState<number>(0)
	const [callbackValues, setCallbackValues] = useState<Record<number, string>>(
		{}
	)

	// ===== PERSISTENT STEP STORAGE =====
	const stepsHistory = useRef<Map<number, StoredStep>>(new Map())
	const journeyId = useRef<string>(generateJourneyId())
	const navigationMethods = useRef<StepNavigationMethods | null>(null)
	const hasInitialized = useRef<boolean>(false)

	// ===== LOGGING UTILITIES =====
	const logStepInfo = useCallback(createLogStepInfo(journeyId.current), [])
	const logNavigationAttempt = useCallback(
		createLogNavigationAttempt(journeyId.current, stepsHistory.current),
		[]
	)
	const logStepsHistory = useCallback(
		createLogStepsHistory(stepsHistory.current),
		[]
	)

	// ===== STEP MANAGEMENT METHODS =====
	const storeStep = useCallback(
		(
			stepNumber: number,
			step: ForgeRockStep,
			values: Record<number, string> = {}
		) => {
			const storedStep: StoredStep = {
				stepNumber,
				step,
				callbackValues: { ...values },
				timestamp: Date.now(),
				stepType: step.type
			}

			stepsHistory.current.set(stepNumber, storedStep)
			logStepInfo("STEP_STORED", stepNumber, step, {
				valuesCount: Object.keys(values).length
			})
			return storedStep
		},
		[logStepInfo]
	)

	const saveCurrentStepState = useCallback(() => {
		if (currentStep) {
			storeStep(currentStepNumber, currentStep, callbackValues)
		}
	}, [currentStep, currentStepNumber, callbackValues, storeStep])

	const navigateToStep = useCallback(
		(targetStepNumber: number): boolean => {
			const targetStep = stepsHistory.current.get(targetStepNumber)

			if (!targetStep) {
				logNavigationAttempt(
					"NAVIGATE_TO_STEP",
					currentStepNumber,
					targetStepNumber,
					false,
					"Step not found in history"
				)
				return false
			}

			// Save current state before navigation
			saveCurrentStepState()

			// Navigate to target step
			setCurrentStepNumber(targetStepNumber)
			setCurrentStep(targetStep.step)
			setCallbackValues(targetStep.callbackValues)

			logNavigationAttempt(
				"NAVIGATE_TO_STEP",
				currentStepNumber,
				targetStepNumber,
				true
			)
			logStepInfo("NAVIGATED_TO", targetStepNumber, targetStep.step)

			return true
		},
		[currentStepNumber, saveCurrentStepState, logNavigationAttempt, logStepInfo]
	)

	const goToPreviousStep = useCallback((): boolean => {
		const previousStepNumber = currentStepNumber - 1
		if (
			previousStepNumber < 0 ||
			!stepsHistory.current.has(previousStepNumber)
		) {
			logNavigationAttempt(
				"GO_PREVIOUS",
				currentStepNumber,
				previousStepNumber,
				false,
				"No previous step available"
			)
			return false
		}
		return navigateToStep(previousStepNumber)
	}, [currentStepNumber, navigateToStep, logNavigationAttempt])

	const goToNextStep = useCallback((): boolean => {
		const nextStepNumber = currentStepNumber + 1
		if (!stepsHistory.current.has(nextStepNumber)) {
			logNavigationAttempt(
				"GO_NEXT",
				currentStepNumber,
				nextStepNumber,
				false,
				"No next step available"
			)
			return false
		}
		return navigateToStep(nextStepNumber)
	}, [currentStepNumber, navigateToStep, logNavigationAttempt])

	const goToInitialStep = useCallback((): boolean => {
		return navigateToStep(0)
	}, [navigateToStep])

	const getCurrentStepNumber = useCallback((): number => {
		return currentStepNumber
	}, [currentStepNumber])

	const getStepsHistory = useCallback((): StoredStep[] => {
		return Array.from(stepsHistory.current.values()).sort(
			(a, b) => a.stepNumber - b.stepNumber
		)
	}, [])

	const getAvailableSteps = useCallback((): number[] => {
		return Array.from(stepsHistory.current.keys()).sort((a, b) => a - b)
	}, [])

	const canNavigateBack = useCallback((): boolean => {
		return (
			currentStepNumber > 0 && stepsHistory.current.has(currentStepNumber - 1)
		)
	}, [currentStepNumber])

	const canNavigateForward = useCallback((): boolean => {
		return stepsHistory.current.has(currentStepNumber + 1)
	}, [currentStepNumber])

	const resetJourney = useCallback(async (): Promise<void> => {
		resetJourneyCache()

		stepsHistory.current.clear()
		setCurrentStepNumber(0)
		setCurrentStep(null)
		setCallbackValues({})
		journeyId.current = generateJourneyId()
		hasInitialized.current = false

		try {
			const initialStep = await ForgeRockAuthService.startJourney()

			// Store the initial step
			storeStep(0, initialStep, {})
			setCurrentStepNumber(0)
			setCurrentStep(initialStep)
			setCallbackValues({})

			hasInitialized.current = true
			logStepInfo("JOURNEY_RESET_AND_INITIALIZED", 0, initialStep)
			logStepsHistory()
		} catch (error) {
			onError?.(error as Error)
		}
	}, [storeStep, logStepInfo, logStepsHistory, onError])

	// ===== STEP REFERENCE METHODS (NO NAVIGATION) =====
	const getInitialStep = useCallback((): StoredStep | null => {
		const initialStep = stepsHistory.current.get(0)
		return initialStep || null
	}, [])

	const getStep = useCallback((stepNumber: number): StoredStep | null => {
		const step = stepsHistory.current.get(stepNumber)
		return step || null
	}, [])

	const getCurrentStep = useCallback((): StoredStep | null => {
		const currentStoredStep = stepsHistory.current.get(currentStepNumber)
		return currentStoredStep || null
	}, [currentStepNumber])

	const initializeJourney = useCallback(async (): Promise<void> => {
		// Prevent re-initialization if already initialized
		if (hasInitialized.current) {
			return
		}

		try {
			const initialStep = await ForgeRockAuthService.startJourney()

			// Store the initial step
			storeStep(0, initialStep, {})
			setCurrentStepNumber(0)
			setCurrentStep(initialStep)
			setCallbackValues({})

			hasInitialized.current = true
			logStepInfo("JOURNEY_INITIALIZED", 0, initialStep)
			logStepsHistory()
		} catch (error) {
			onError?.(error as Error)
		}
	}, [storeStep, logStepInfo, logStepsHistory, onError])

	// ===== CALLBACK MANAGEMENT =====
	const updateCallbackValue = useCallback(
		(index: number, value: string): void => {
			setCallbackValues((prev) => {
				const updated = {
					...prev,
					[index]: value
				}

				// Auto-save callback values when they change
				if (currentStep) {
					storeStep(currentStepNumber, currentStep, updated)
				}

				return updated
			})
		},
		[currentStep, currentStepNumber, storeStep]
	)

	// Initialize navigation methods reference
	useEffect(() => {
		navigationMethods.current = {
			navigateToStep,
			goToPreviousStep,
			goToNextStep,
			goToInitialStep,
			getCurrentStepNumber,
			getStepsHistory,
			getAvailableSteps,
			canNavigateBack,
			canNavigateForward,
			resetJourney,
			// Step reference methods
			getInitialStep,
			getStep,
			getCurrentStep
		}

		exposeNavigationMethodsToWindow(navigationMethods.current)
	}, [
		navigateToStep,
		goToPreviousStep,
		goToNextStep,
		goToInitialStep,
		getCurrentStepNumber,
		getStepsHistory,
		getAvailableSteps,
		canNavigateBack,
		canNavigateForward,
		resetJourney,
		getInitialStep,
		getStep,
		getCurrentStep
	])

	// Initialize journey on mount (unless skipInitialJourney is true)
	useEffect(() => {
		if (!skipInitialJourney && !hasInitialized.current) {
			initializeJourney()
		}
	}, [initializeJourney, skipInitialJourney])

	return {
		// State
		currentStep,
		currentStepNumber,
		callbackValues,

		// Navigation methods
		navigationMethods: navigationMethods.current,

		// Step management
		storeStep,
		saveCurrentStepState,

		// Callback management
		updateCallbackValue,

		// Utilities
		logStepInfo,
		logStepsHistory,

		// Journey management
		initializeJourney,
		resetJourney,

		// Journey ID for external reference
		journeyId: journeyId.current
	}
}
